import React, {ReactNode, useCallback, useEffect, useRef, useState} from "react";
import { useSearchParams } from "react-router-dom";
import Table from "./table";
import Pagination from "../../pagination/pagination";
import styles from "../data-table.module.scss";
import { TableData } from "../../../../types/table-filter-types";
import {
  filterTableData,
  TableFilters,
} from "../../../../utils/filters-for-table";

interface TableContainerState {
  selected: number[];
  filters: TableFilters;
  searchValue: string;
  isLoading: boolean;
  currentPage: number;
  totalDataSize: number;
  handlePageChange: (page: number) => void;
  handleSearch: () => void;
  setFilters: (filters: TableFilters) => void;
  setSearchValue: (value: string) => void;
  substringFields: string[];
}

interface TableContainerProps {
  children: ReactNode;
  dataSource: TableData;
  rowHeight: number;
  dateTemplate?: string;
  tableName: string;
  tableIcon: string;
  debounceMs?: number;
  idField: string;
  footerContent?: (tableState: TableContainerState) => ReactNode;
  headerContent?: (tableState: TableContainerState) => ReactNode;
  visibleFilter?: boolean
  data: TableData;
  totalDataSize: number;
  selected?: number[];
  setData: (payload: { data: TableData, page: number, totalDataSize: number, pageSize: number }) => void;
  addSelected?: (ids: number[]) => void;
  deleteSelected?: (ids: number[]) => void;
  deleteAllSelected?: () => void;
  placeholderSearch: string;
  substringFields: string[]
  renderRowMenu?: (rowData: Record<string, any>) => React.ReactNode;
  secondLineOpacity?: boolean;
  onPageSizeChange?: (pageSize: number) => void
  isLoading: boolean;
  visibleSelect?: boolean;
  onRowClick?: (rowData: Record<string, any>) => void;
  getRowColor?: (rowData: Record<string, any>) => string | undefined
}
//TODO Если будет возможность, то нужно переписать компонент таблицы
const TableContainer: React.FC<TableContainerProps> = ({
  children,
  dataSource,
  rowHeight,
  dateTemplate,
  tableName,
  tableIcon,
  debounceMs = 0,
  idField,
  footerContent,
  headerContent,
  visibleFilter = true,
  data,
  totalDataSize,
  selected,
  setData,
  addSelected,
  deleteSelected,
  deleteAllSelected,
  placeholderSearch,
  substringFields,
  renderRowMenu,
  secondLineOpacity = false,
  onPageSizeChange,
  isLoading,
  visibleSelect = true,
  onRowClick,
  getRowColor,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get("page")) || 1;
  const [openedPopup, setOpenedPopup] = useState<string>("");
  const [filters, setFilters] = useState<TableFilters>({});
  const [searchValue, setSearchValue] = useState<string>("")

  // refs for calculate pageSize
  const mainRef = useRef<HTMLDivElement | null>(null);
  const mainTitleRef = useRef<HTMLDivElement | null>(null);
  const filtersTitleRef = useRef<HTMLElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const tableHeaderRef = useRef<HTMLTableSectionElement | null>(null);
  const paginationRef = useRef<HTMLDivElement | null>(null);
  const searchAndFiltersRef = useRef<HTMLDivElement | null>(null);
  const activeFiltersRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const calculatePageSize = () => {
    const mainTitleHeight = mainTitleRef.current?.offsetHeight,
      mainTitleMarginBottom = mainTitleRef.current
        ? Number(
            window
              .getComputedStyle(mainTitleRef.current)
              .marginBottom.slice(0, -2),
          )
        : 0,
      mainTitleTotalHeight = mainTitleHeight! + mainTitleMarginBottom,
      searchAndFiltersHeight = searchAndFiltersRef.current?.offsetHeight,
      searchAndFiltersMarginBottom = searchAndFiltersRef.current
        ? Number(
            window
              .getComputedStyle(searchAndFiltersRef.current)
              .marginBottom.slice(0, -2),
          )
        : 0,
      searchAndFiltersTotalHeight = searchAndFiltersHeight! + searchAndFiltersMarginBottom

      let activeFiltersHeight = 0 
      let activeFiltersMarginBottom = 0
      let activeFiltersTotalHeight = 0

      if (activeFiltersRef.current?.offsetHeight) {
        activeFiltersHeight = activeFiltersRef.current?.offsetHeight
        activeFiltersMarginBottom = activeFiltersRef.current
          ? Number(
              window
                .getComputedStyle(activeFiltersRef.current)
                .marginBottom.slice(0, -2),
            )
          : 0
  
        activeFiltersTotalHeight = activeFiltersHeight! + activeFiltersMarginBottom;
      }

    const tableHeaderHeight = tableHeaderRef.current?.offsetHeight;

    const paginationHeight = paginationRef.current?.offsetHeight,
      paginationMarginBottom = paginationRef.current
        ? Number(
            window
              .getComputedStyle(paginationRef.current)
              .marginBottom.slice(0, -2),
          )
        : 0,
      paginationTotalHeight = paginationHeight! + paginationMarginBottom;

    const tableMarginBottom = tableRef.current
      ? Number(
          window.getComputedStyle(tableRef.current!).marginBottom.slice(0, -2),
        )
      : 0;

    const heightsSum =
      mainTitleTotalHeight +
      tableHeaderHeight! +
      paginationTotalHeight +
      tableMarginBottom +
      activeFiltersTotalHeight +
      searchAndFiltersTotalHeight;

    return Math.floor(
      (mainRef.current?.offsetHeight
        ? mainRef.current?.offsetHeight - heightsSum
        : 0) / (rowHeight > 41.3 ? rowHeight : 41.3),
    );
  };

  const setPageInURL = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", String(page));
    setSearchParams(params);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (isLoading || dataSource.length === 0) {
        return;
      }

      const pageSize = calculatePageSize();

      if (onPageSizeChange) {
        onPageSizeChange(pageSize);
      }

      const result = filterTableData(dataSource, filters, currentPage, pageSize, substringFields);

      setData({
        data: result.data,
        totalDataSize: result.totalDataSize,
        page: result.currentPage,
        pageSize: pageSize,
      });

      setPageInURL(result.currentPage);
    };

    fetchData();
  }, [dataSource, currentPage, isLoading, filters]);

  const handlePageChange = useCallback((page: number) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const params = new URLSearchParams(searchParams);
      params.set("page", String(page));
      setSearchParams(params, { replace: true });
    }, debounceMs);
  }, [searchParams]);

  const handleSearch = () => {
    const trimmedValue = searchValue!.trim(); // Убираем пробелы в начале и конце строки
    if (trimmedValue) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        searchSubstring: trimmedValue, // Добавляем новое значение в фильтры
      }));
      setSearchValue(""); // Очищаем строку поиска
    }
  };

  return (
    <div className={styles.tableContainer}>
      <Table
        idField={idField}
        data={data}
        isLoading={isLoading}
        rowHeight={rowHeight}
        dateTemplate={dateTemplate}
        tableName={tableName}
        tableIcon={tableIcon}
        currentPage={currentPage}
        pageSize={calculatePageSize()}
        totalDataSize={totalDataSize}
        handlePageChange={handlePageChange}
        openedPopup={openedPopup}
        mainRef={mainRef}
        setOpenedPopup={setOpenedPopup}
        mainTitleRef={mainTitleRef}
        filtersTitleRef={filtersTitleRef}
        tableRef={tableRef}
        tableHeaderRef={tableHeaderRef}
        paginationRef={paginationRef}
        debounceMs={debounceMs}
        searchAndFiltersRef={searchAndFiltersRef}
        activeFiltersRef={activeFiltersRef}
        onSearchChange={(e) => setSearchValue(e.target.value)}
        onSearch={handleSearch}
        searchValue={searchValue}
        filters={filters}
        setFilters={setFilters}
        setSearchValue={setSearchValue}
        headerContent={headerContent && headerContent({
          selected: selected ?? [],
          filters,
          searchValue,
          isLoading,
          currentPage,
          totalDataSize,
          handlePageChange,
          handleSearch,
          setFilters,
          setSearchValue,
          substringFields
        })}
        visibleFilter={visibleFilter}
        selected={selected}
        addSelected={addSelected}
        deleteSelected={deleteSelected}
        deleteAllSelected={deleteAllSelected}
        placeholderSearch={placeholderSearch}
        renderRowMenu={renderRowMenu}
        secondLineOpacity={secondLineOpacity}
        visibleSelect={visibleSelect}
        onRowClick={onRowClick}
        getRowColor={getRowColor}
      >
        {children}
      </Table>
      <div className={styles.footer}>
        {footerContent && footerContent({
          selected: selected ?? [],
          filters,
          searchValue,
          isLoading,
          currentPage,
          totalDataSize,
          handlePageChange,
          handleSearch,
          setFilters,
          setSearchValue,
          substringFields
        })}
        <Pagination
          pageSize={calculatePageSize()}
          totalDataSize={totalDataSize}
          onChange={handlePageChange}
          currentPage={currentPage}
          isPageLoading={isLoading}
          paginationRef={paginationRef}
        />
      </div>
    </div>
  );
};

export default TableContainer;
